import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { useLangNavigate, useT } from '@/i18n';
import { motion } from 'framer-motion';
import { User, LogOut, Trophy, Clock, AlertCircle, ChevronRight } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { AgencyLinks } from '@/components/layout/AgencyLinks';
import { MobileContainer } from '@/components/layout/MobileContainer';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { useGameStore } from '@/store/useGameStore';
import { MODULES } from '@/data/moduleData';

export default function ProfileScreen() {
  const navigate = useLangNavigate();
  const t = useT();
  const { userName, moduleProgress, logout } = useGameStore();

  const completedModules = Object.values(moduleProgress).filter(m => m.status === 'completed').length;
  const allAttempts = Object.values(moduleProgress).flatMap(p => p.attempts || []);
  const totalErrors = allAttempts.reduce((sum, a) => sum + a.errors, 0);
  const totalTime = allAttempts.reduce((sum, a) => sum + a.timeSeconds, 0);
  const bestRun = allAttempts.length > 0 ? Math.min(...allAttempts.map(a => a.errors)) : null;

  const handleLogout = () => {
    if (window.confirm(t('ui.profile.logoutConfirm'))) {
      logout();
      navigate('/', { replace: true });
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    return `${mins} ${t('ui.common.min')}`;
  };

  return (
    <MobileContainer noPadding>
      <div className="flex-1 flex flex-col overflow-y-auto scrollbar-hide">
        {/* Profile header */}
        <div className="gradient-hero px-5 pt-8 pb-10 rounded-b-[32px]">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center"
          >
            <Avatar className="h-20 w-20 border-4 border-primary-foreground/30 mb-3">
              <AvatarFallback className="bg-card/20 text-primary-foreground font-heading font-extrabold text-2xl">
                {userName?.[0]?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <h1 className="text-xl font-extrabold font-heading text-primary-foreground">
              {userName}
            </h1>
            <p className="text-sm text-primary-foreground/70 font-body">{t('ui.profile.role')}</p>
            <LanguageSwitcher className="justify-center mt-3" variant="light" />
          </motion.div>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="px-5 -mt-6 relative z-10 mb-4"
        >
          <Card className="p-4 shadow-card">
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center">
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-1">
                  <Trophy size={16} className="text-primary" />
                </div>
                <p className="text-base font-bold font-heading text-foreground">{completedModules}</p>
                <p className="text-[10px] text-muted-foreground font-body">{t('ui.profile.statModules')}</p>
              </div>
              <div className="text-center">
                <div className="w-9 h-9 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-1">
                  <Clock size={16} className="text-secondary" />
                </div>
                <p className="text-base font-bold font-heading text-foreground">{formatTime(totalTime)}</p>
                <p className="text-[10px] text-muted-foreground font-body">{t('ui.profile.statTime')}</p>
              </div>
              <div className="text-center">
                <div className="w-9 h-9 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-1">
                  <AlertCircle size={16} className="text-success" />
                </div>
                <p className="text-base font-bold font-heading text-foreground">
                  {bestRun !== null ? bestRun : '-'}
                </p>
                <p className="text-[10px] text-muted-foreground font-body">{t('ui.profile.statBest')}</p>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Menu items */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="px-5"
        >
          <Card className="shadow-soft overflow-hidden">
            {[
              { label: t('ui.common.myProgress'), icon: Trophy, onClick: () => navigate('/progress') },
              { label: t('ui.common.startTraining'), icon: User, onClick: () => navigate('/module/module_1') },
            ].map((item, idx) => (
              <div key={idx}>
                <button
                  onClick={item.onClick}
                  className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-muted/50 transition-colors duration-150"
                >
                  <item.icon size={18} className="text-muted-foreground" />
                  <span className="text-sm font-medium font-body text-foreground flex-1 text-left">{item.label}</span>
                  <ChevronRight size={16} className="text-muted-foreground" />
                </button>
                {idx < 1 && <Separator />}
              </div>
            ))}
          </Card>
        </motion.div>

        {/* Logout */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="px-5 mt-6 pb-8"
        >
          <Button
            variant="outline"
            className="w-full h-11 rounded-xl border-2 border-destructive/20 text-destructive hover:bg-destructive/5 font-heading font-semibold text-sm"
            onClick={handleLogout}
          >
            <LogOut size={16} className="mr-2" />
            {t('ui.profile.logout')}
          </Button>
          <AgencyLinks className="mt-6" />
        </motion.div>
      </div>

      <BottomNavigation />
    </MobileContainer>
  );
}
